import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import tw from 'twin.macro'
import { useDispatch, useSelector } from 'react-redux'
import { Payment } from 'src/interfaces/payment'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { FetchPayments } from 'src/constants/actionTypes'
import { paymentPageSizeOptions } from 'src/constants/payment/paymentGrid'
import { IPaymentsTableContext, PaymentsTableContext } from './PaymentsTable.context'
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getAccessInfo, getPartnerId } from 'src/selectors/user'
import { getSelectedMarket } from 'src/selectors/market'
import { PaymentsHeader } from '../grid/PaymentsHeader'
import { PaymentsItemActionMenu } from '../grid/action/PaymentsItemActionMenu'
import { PaymentStatus } from 'src/constants/payment/enums'
import { handleApiError } from 'src/utils/errorHandlers'
import { useLocation } from 'react-router-dom'
import _ from 'lodash'
import { Pagination } from 'src/interfaces/table'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { paymentColumns } from 'src/constants/payment/paymentGrid'
import { EmptyPayments } from '../grid/EmptyPayments'
import { UserProductName } from 'src/constants/user'
import { PaymentsButtons } from '../PaymentsButtons'
import { NoteMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'

interface FetchPaymentsParams {
  limit?: number
  offset?: number
  searchQuery?: string
  tagsFilter?: Array<string>
  sortModel?: GridSortModel
}

const getRowId = (row: Payment) => row.id

export interface PaymentsTableRef {
  refreshPayments: () => Promise<void>
}

interface Props {
  onRefreshMCPayments: () => Promise<void>
}

const PaymentsTableComponent = forwardRef<PaymentsTableRef, Props>(
  ({ onRefreshMCPayments }, ref) => {
    const dispatch = useDispatch()
    const { state } = useLocation()

    const stateFilterRef = useRef<Array<PaymentStatus>>([])

    const partnerId = useSelector(getPartnerId)
    const selectedMarket = useSelector(getSelectedMarket)
    const accessInfo = useSelector(getAccessInfo)

    const hasSubcontractorProduct = useMemo(
      () => accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor),
      [accessInfo],
    )

    const { paginationModel, limit, offset, setPaginationModel } =
      useGridPagination(paymentPageSizeOptions)

    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [_payments, setPayments] = useState<Array<Payment>>([])
    const [totalPayments, setTotalPayments] = useState<number>(0)
    const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null)
    const [paymentToAction, setPaymentToAction] = useState<Payment | null>(null)
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([])
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [statusFilter, setStatusFilter] = useState<Array<PaymentStatus>>(
      state?.statusFilter || [],
    )
    const [sortModel, setSortModel] = useState<GridSortModel>([
      { field: 'uploadedAt', sort: 'desc' },
    ])
    const [notPaidPaymentsAmount, setNotPaidPaymentsAmount] = useState<number>(0)

    const payments = useMemo(() => _.uniqBy(_payments, 'id'), [_payments])

    useEffect(() => {
      if (!state?.statusFilter || _.isEqual(state?.statusFilter, stateFilterRef.current)) {
        return
      }

      stateFilterRef.current = state?.statusFilter
      setStatusFilter(state?.statusFilter)
    }, [state?.statusFilter])

    const selectedPayments = useMemo<Array<Payment>>(() => {
      return selectedRows.reduce<Array<Payment>>((arr, selectedRowId) => {
        const payment = payments.find((item) => item.id === selectedRowId)

        if (payment) {
          arr.push(payment)
        }

        return arr
      }, [])
    }, [payments, selectedRows])

    const fetchPayments = useCallback(
      async ({ limit, offset, searchQuery, sortModel }: FetchPaymentsParams) => {
        if (!partnerId) {
          return
        }

        setIsLoading(true)

        try {
          const params: any = {
            partnerId,
            limit,
            offset,
            paymentStatus: statusFilter,
          }

          if (searchQuery) {
            params.search = searchQuery
          }

          if (selectedMarket?.id) {
            params.partnerMarketId = selectedMarket?.id
          }

          if (sortModel && sortModel[0]) {
            params.sort = `${sortModel[0].field}:${sortModel[0].sort?.toUpperCase()}`
          }

          const payload: { data: Array<Payment>; pagination: Pagination } = await createAsyncAction(
            dispatch,
            FetchPayments.request(params),
          )

          setPayments(payload.data)
          setTotalPayments(payload.pagination.total)
          setNotPaidPaymentsAmount(
            payload.data.filter((item) => item.status === PaymentStatus.NotPaid).length,
          )
        } catch (err: unknown) {
          handleApiError(err)
        }

        setIsLoading(false)
      },
      [partnerId, statusFilter, selectedMarket?.id],
    )

    useEffect(() => {
      fetchPayments({
        limit,
        offset,
        searchQuery,
        sortModel,
      })
    }, [limit, offset, searchQuery, partnerId, sortModel, statusFilter, selectedMarket?.id])

    const refreshPayments = useCallback(async () => {
      await fetchPayments({ limit, offset, searchQuery, sortModel })
    }, [limit, offset, searchQuery, sortModel, fetchPayments])

    const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
      setPaginationModel(paginationModel)
    }, [])

    const openPaymentsItemAction = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>, paymentToEdit: Payment) => {
        setActionAnchor(event.currentTarget)

        setPaymentToAction(paymentToEdit)
      },
      [],
    )

    const closePaymentsItemAction = useCallback(() => {
      setActionAnchor(null)
    }, [])

    const clearPaymentsItemAction = useCallback(() => {
      setActionAnchor(null)
      setPaymentToAction(null)
    }, [])

    const handleSelectStatus = useCallback((statuses: Array<PaymentStatus>) => {
      setStatusFilter(statuses.slice(statuses.length - 1, statuses.length))
      setSelectedRows([])
    }, [])

    const handleSortModelChange = (newModel: GridSortModel) => {
      setSortModel(newModel)
    }

    const paymentsTableContextValue = useMemo<IPaymentsTableContext>(() => {
      return {
        notPaidPaymentsAmount,

        selectedRows,
        setSelectedRows,

        selectedPayments,

        searchQuery,
        setSearchQuery,

        statusFilter,
        setStatusFilter: handleSelectStatus,

        actionAnchor,
        paymentToAction,
        openPaymentsItemAction,
        closePaymentsItemAction,
        clearPaymentsItemAction,
      }
    }, [
      notPaidPaymentsAmount,

      selectedRows,
      setSelectedRows,

      selectedPayments,

      searchQuery,
      setSearchQuery,

      statusFilter,
      handleSelectStatus,

      actionAnchor,
      paymentToAction,
      openPaymentsItemAction,
      closePaymentsItemAction,
      clearPaymentsItemAction,
    ])

    useImperativeHandle(
      ref,
      () => ({
        refreshPayments,
      }),
      [refreshPayments],
    )

    return (
      <PaymentsTableContext.Provider value={paymentsTableContextValue}>
        <div css={[tw`flex items-end w-full justify-end`, hasSubcontractorProduct && tw` mb-4`]}>
          <a href="/payments_example.csv" download="payments_example.csv">
            <Button
              className={tw`mr-4`}
              size="small"
              color="white"
              rightIcon={<NoteMinor width={20} height={20} />}
            >
              {hasSubcontractorProduct
                ? 'Download contractors CSV template'
                : 'Download CSV template'}
            </Button>
          </a>
          <PaymentsButtons
            onRefreshPayments={refreshPayments}
            onRefreshMCPayments={onRefreshMCPayments}
            isMCselected={false}
          />
        </div>
        <PaymentsHeader />
        <GridTable<Payment>
          checkboxSelection
          rows={payments}
          columns={paymentColumns}
          isLoading={isLoading}
          paginationModel={paginationModel}
          pageSizeOptions={paymentPageSizeOptions}
          rowCount={totalPayments}
          rowSelectionModel={selectedRows}
          sortModel={sortModel}
          className={[
            {
              '@media (min-height: 830px)': tw`h-[590px]`,
              '@media (min-height: 1000px)': tw`h-[770px]`,
              '@media (min-height: 1440px)': tw`h-[1170px]`,
              '@media (min-height: 2000px)': tw`h-[1570px]`,
            },
          ]}
          EmptyElement={EmptyPayments}
          onSortModelChange={handleSortModelChange}
          onPaginationModelChange={handleChangePagination}
          onRowSelectionModelChange={setSelectedRows}
          getRowId={getRowId}
        />
        <PaymentsItemActionMenu />
      </PaymentsTableContext.Provider>
    )
  },
)

export const PaymentsTable = memo(PaymentsTableComponent)
